import classNames from 'classnames';
import React, { memo, useContext, useState } from 'react';
import { Blok } from '../../../interfaces/Blok.interface';
import { StoryblokLink } from '../../../interfaces/StoryblokLink.interface';
import HeaderContext from '../../../contexts/Header.context';
import { PageContext } from '../../../contexts/PageContext';
import { Theme } from '../../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../../BlokWrapper/BlokWrapper';
import MainNavItem from './MainNavItem/MainNavItem';
import { MainNavDropdownBlokProps } from './MainNavDropdown/MainNavDropdown';
import CustomLink from '../../CustomLink/CustomLink';
import { VisibleSideNavType } from '../Header';
import Container from '../../Utils/Container/Container';
import Grid from '../../Utils/Grid/Grid';
import Col from '../../Utils/Col/Col';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import { StoryblokAsset } from '../../../interfaces/StoryblokAsset.interface';
import Image from '../../Image/Image';
import SbImage from '../../SbImage/SbImage';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { getAlgoliaPermittedContentTypesFilter } from '../../../utils/getAlgoliaPermittedContentTypesFilter/getAlgoliaPermittedContentTypesFilter.util';
import { useMediaQuery } from 'react-responsive';

export interface MainNavLink extends Blok {
  label: string;
  link: StoryblokLink;
  dropdown: MainNavDropdownBlokProps[];
}

interface MainNavProps {
  company_logo: StoryblokAsset;
  main_navigation_items: MainNavLink[];
  primary_cta: Blok[];
  search?: JSX.Element;
  enable_search: boolean;
  needsTransparentHeader: boolean;
  toggleSideNav: (isVisible: boolean, sideNavType: VisibleSideNavType) => void;
  searchIndexName: string;
  overriddenSearchClient: any;
  searchPermittedContentTypes: string[];
}

const MainNav: React.FC<MainNavProps> = ({
  company_logo,
  main_navigation_items,
  primary_cta,
  enable_search,
  search = undefined,
  toggleSideNav,
  searchIndexName,
  overriddenSearchClient,
  searchPermittedContentTypes,
}) => {
  //
  const { headerIsTransparent, isHeaderSearchFunctionalityRequired } =
    useContext(PageContext);
  const { userHasScrolled, navIsUnpinned } = useContext(HeaderContext);

  const [isDesktopSearchDropdownVisible, setIsDesktopSearchDropdownVisible] =
    useState<boolean>(false);

  const smallHeight = useMediaQuery({
    query: '(max-height: 700px)',
  });

  return (
    <section
      className={classNames({
        'bg-surface/background':
          !headerIsTransparent || userHasScrolled || navIsUnpinned,
        'bg-transparent': headerIsTransparent && !userHasScrolled,
      })}
    >
      <nav
        className="container m-auto flex items-center justify-between w-full py-5 sm:py-0 gap-y-4 sm:gap-y-0 sm:h-[119px] flex-wrap sm:flex-nowrap"
        aria-label="main navigation"
      >
        <CustomLink url="/" label="Home" className="w-[160px] sm:w-[200px]">
          {/* <Image image={company_logo} /> */}
          <SbImage
            asset={company_logo}
            options={{
              width: 225,
              height: 55,
              sizes: { base: 180, sm: 225 },
              loading: 'eager',
            }}
          />
        </CustomLink>
        <div className="flex items-center h-full space-x-4">
          <ul
            className={`hidden xl:flex items-center font-bold text-base space-x-2 2xl:space-x-4 h-full `}
          >
            {main_navigation_items.map(({ ...props }) => (
              <MainNavItem {...props} key={props._uid} />
            ))}
          </ul>
          <div className="flex flex-row-reverse items-center gap-x-4 lg:flex-row">
            {isHeaderSearchFunctionalityRequired && enable_search && (
              <button
                className="h-[32px] w-[32px] bg-no-repeat bg-[white] hover:bg-text/body-accent transition-all"
                style={{
                  WebkitMaskImage: "url('/svgs/search.svg')",
                  maskImage: "url('/svgs/search.svg')",
                }}
                aria-label="search"
                onClick={() => {
                  toggleSideNav(true, VisibleSideNavType.Search);
                  setIsDesktopSearchDropdownVisible(
                    !isDesktopSearchDropdownVisible
                  );
                }}
              ></button>
            )}
            {primary_cta.map((cta: Blok) => (
              <div className="hidden sm:block" key={cta._uid}>
                <StoryblokComponent blok={cta} />
              </div>
            ))}
          </div>
          <button
            className="block xl:hidden h-[40px] w-[40px] bg-[url('/svgs/hamburger.svg')] bg-no-repeat"
            aria-label="open menu"
            onClick={() => toggleSideNav(true, VisibleSideNavType.Menu)}
          ></button>
        </div>
        {primary_cta.map((cta: Blok) => (
          <div className="block sm:hidden w-full" key={cta._uid}>
            <StoryblokComponent blok={cta} />
          </div>
        ))}
      </nav>
      {/*-- search (desktop only. for mobile, see inside SideNav component --*/}
      {isHeaderSearchFunctionalityRequired && enable_search && (
        <div
          className={classNames('hidden', {
            'xl:block xl:bg-system/white xl:py-12 xl:border-b xl:border-b-border/light':
              isDesktopSearchDropdownVisible,
            'h-[calc(100vh-119px)] overflow-y-scroll': smallHeight,
          })}
        >
          <Container>
            <Grid>
              <Col span={{ lg: 8 }} start={{ lg: 3 }}>
                <InstantSearch
                  indexName={searchIndexName}
                  searchClient={overriddenSearchClient}
                >
                  <Configure
                    filters={`${getAlgoliaPermittedContentTypesFilter(
                      searchPermittedContentTypes
                    )}`}
                    hitsPerPage={3}
                  />
                  {search}
                </InstantSearch>
              </Col>
            </Grid>
          </Container>
        </div>
      )}
    </section>
  );
};

export default memo(withBlokWrapper(MainNav, Theme.DARK));
